import React, {useContext, useEffect, useState} from 'react';
import * as Builder from "../../../builder";
import {ButtonBar} from "../../../builder";
import {MaxLengthConstraint, RequiredConstraint} from "../../../builder/form/validation/constraints";
import {Button} from "antd";
import {Translate} from "../../../components/util/translation.util";
import {TranslationDomainEnum} from "../../../types/enums";
import {observer} from "mobx-react";
import {StoreContext} from "../../../store.context";
import {useLoaderData, useNavigate, useParams} from "react-router-dom";
import {UserEditRequest} from "../../../types/modules/user/request";
import {TextType} from "../../../builder/form/type/TextType";
import {CheckboxType} from "../../../builder/form/type/CheckboxType";
import {EntityType} from "../../../builder/form/type/EntityType";
import {EmailType} from "../../../builder/form/type/EmailType";
import {ConfirmButton} from "../../../components/button/confirm.button";
import {PasswordType} from "../../../builder/form/type/PasswordType";
import {UserPrepareEditFormResponse} from "../../../types/modules/user/response";
import {Helmet} from "react-helmet-async";

export const EditForm: React.FC<{ onCancel?: () => void }> = observer(({onCancel}) => {
    const navigate = useNavigate();
    const params = useParams();
    const formReference = React.createRef() as any;
    const data: UserPrepareEditFormResponse = useLoaderData() as UserPrepareEditFormResponse;

    const [fetching, setFetching] = useState<boolean>(true)
    const [classifiers, setClassifiers] = useState<any>([]);
    const [formErrors, setFormErrors] = useState<any>([]);

    const {appStore: {moduleStore: {userStore: {edit, resetOtp}}}} = useContext(StoreContext);

    useEffect(() => {
        setFetching(false);
        setClassifiers(data.classifiers);
        formReference.current?.setData(data.data);
    }, [data])

    const onSubmit = (values: UserEditRequest) => {
        setFetching(true);
        edit({...values, id: params.id})
                .then((data) => {
                    navigate(`/user/${data.id}/view`);
                })
                .catch(error => {
                    setFormErrors(error.response.data.errors);
                })
                .finally(() => {
                    setFetching(false);
                })
    }

    return (
            <>
                <Helmet>
                    <title>{`CL BO - ${data?.extraData?.username}`}</title>
                </Helmet>
                <Builder.Form
                        translationDomain={TranslationDomainEnum.USER_ADMINISTRATION}
                        formErrors={formErrors}
                        ref={formReference}
                        items={[
                            {
                                title: <Translate message={`global.title.edit`}/>,
                                position: 'left',
                                inputs: [
                                    {
                                        name: 'credentials.currentPassword',
                                        label: 'user.field.currentPassword',
                                        type: PasswordType,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'credentials.otp',
                                        label: 'user.field.otp',
                                        type: TextType,
                                    },
                                    {
                                        name: 'firstName',
                                        label: 'user.field.firstName',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(150),
                                        ]
                                    },
                                    {
                                        name: 'lastName',
                                        label: 'user.field.lastName',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(150),
                                        ],
                                    },
                                    {
                                        name: 'initials',
                                        label: 'user.field.initials',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(20),
                                        ],
                                    },
                                    {
                                        name: 'additions',
                                        label: 'user.field.additions',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(50),
                                        ],
                                    },
                                    {
                                        name: 'email',
                                        label: 'user.field.email',
                                        type: EmailType,
                                        constraints: [
                                            MaxLengthConstraint(128),
                                            RequiredConstraint(),
                                        ],
                                    },
                                    {
                                        name: 'disabled',
                                        label: 'user.field.disabled',
                                        type: CheckboxType
                                    },
                                    {
                                        name: 'userRoles',
                                        label: 'user.field.userRoles',
                                        type: EntityType,
                                        choices: classifiers.roles,
                                        multiple: true,
                                    },
                                ]
                            }
                        ]}
                        onFinish={(values: any) => {
                            onSubmit(values)
                        }}
                />

                <ButtonBar>
                    <Button type="primary" loading={fetching} htmlType={`submit`} onClick={() => {
                        formReference.current?.submitForm()
                    }}><Translate message={`global.button.save`}/></Button>

                    <Button type="primary" className={`ant-btn-secondary`} onClick={() => navigate(`/user/${params.id}/view`)}>
                        <Translate message={`global.button.view`}/>
                    </Button>

                    <Button type="primary" className={`ant-btn-secondary`} onClick={() => navigate(`/user/${params.id}/password/edit`)}>
                        <Translate message={`user.button.change_password`} domain={TranslationDomainEnum.USER_ADMINISTRATION}/>
                    </Button>

                    <ConfirmButton message={`user.button.reset_otp`} domain={TranslationDomainEnum.USER_ADMINISTRATION} onClick={async () => {
                        await resetOtp({id: params.id})
                    }}/>

                    <Button type="primary" className={`ant-btn-secondary`} onClick={() => {
                        onCancel && onCancel()
                    }}><Translate message={`global.button.back_to_list`}/></Button>
                </ButtonBar>
            </>
    )
})